/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import axios from 'axios'
import {useAuth} from '../../modules/auth'
import ClaimCard from './ClaimCard'
import {ToolbarClassic} from '../../../_metronic/layout/components/toolbar/toolbars/ToolbarClassic'
import {HiOutlineLightningBolt} from 'react-icons/hi'
import {IoMdTime} from 'react-icons/io'
import {LuLayoutPanelLeft} from 'react-icons/lu'
import {useNavigate} from 'react-router-dom'
import ChatModal from '../layout-inpatient/tab-components/ChatModal'
import {toast} from 'react-toastify'
import {GoSortAsc, GoSortDesc} from 'react-icons/go'
import {IoDocumentTextOutline} from 'react-icons/io5'

interface Claim {
  id: string
  memberName: string
  claimNumber: string
  cardNumber: string
  policyNumber: string
  customerGroup: string
  status: string
  claimDate: string
  claimType: string
  treatmentType: string
}

const DashboardPage: FC = () => {
  const navigate = useNavigate()
  const [totalRecord, setTotalRecord] = useState<any>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isChatModalOpen, setIsChatModalOpen] = useState<boolean>(false)
  const [claimDetails, setClaimDetails] = useState<any>(null)
  const [sortOrder, setSortOrder] = useState<'oldest' | 'newest'>('oldest')
  const user = useAuth()
  const {auth} = useAuth()
  const MAX_ITEMS_PER_SECTION = 20
  const [isSorting, setIsSorting] = useState<boolean>(false)

  const {formatMessage: f, locale} = useIntl()

  // Filter items for Red Section (action needed)
  const redSectionStatuses = [
    'Pre-Registered',
    'Pending on IGL',
    'IGL Released',
    'Daily Monitoring',
    // 'Supercreeding request',
    'Waiting Enquiry Response',
    'Pending on FGL',
  ]

  // Filter items for Blue Section (recent)
  const blueSectionStatuses = [
    'New Case',
    'Admission Review',
    'Initial Document Complete',
    'Request FGL',
    'FGL Review',
    'FGL Released',
    'Final Document Complete',
  ]

  // Filter, sort, and limit items for Red Section - sort by older dates first
  const redSectionItems = totalRecord?.items
    ? totalRecord.items
        .filter((item: any) => redSectionStatuses.includes(item.status))
        .sort((a: any, b: any) => {
          const timeA = new Date(a.updatedAt).getTime()
          const timeB = new Date(b.updatedAt).getTime()
          return sortOrder === 'oldest'
            ? timeA - timeB // Oldest first (ascending)
            : timeB - timeA // Newest first (descending)
        })
        .slice(0, MAX_ITEMS_PER_SECTION)
    : []

  // Filter, sort, and limit items for Blue Section
  const blueSectionItems = totalRecord?.items
    ? totalRecord.items
        .filter((item: any) => blueSectionStatuses.includes(item.status))
        .slice(0, MAX_ITEMS_PER_SECTION)
    : []

  console.log(blueSectionItems)

  // Set loading to false when totalRecord is loaded
  useEffect(() => {
    if (totalRecord) {
      setIsLoading(false)
    }
  }, [totalRecord])

  const fetchClaimDetails = async (claimNumber: string) => {
    // const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL_NGROK}/api/Provider/GetByClaimNumber/${claimNumber}`
    const apiUrl = `${process.env.REACT_APP_API_PROVIDER_URL}/api/Claims/GetByClaimNumber/${claimNumber}`
    try {
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${auth?.token} || ${user.currentUser?.token}`,
        },
      })
      return response.data
    } catch (error) {
      console.error('Error fetching claim details:', error)
      return null
    }
  }

  const handleChatAction = async (claim: Claim) => {
    const details = await fetchClaimDetails(claim.claimNumber)

    if (details) {
      setClaimDetails(details)
      setIsChatModalOpen(true)
    }
  }

  const handleClaimAction = async (claim: Claim) => {
    const claimDetails = await fetchClaimDetails(claim.claimNumber)
    if (claimDetails) {
      if (claim.claimType === 'OUTPATIENT' || claim.treatmentType === 'Rawat jalan') {
        navigate('/outpatient', {state: {claimDetails}})
      } else {
        navigate('/inpatient', {state: {claimDetails}})
      }
    }
  }

  const handleDownloadIGL = async (claimNumber: string) => {
    console.log('claimNumber', claimNumber)
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_CLAIM_URL}/api/Attachment/DownloadIGL/${claimNumber}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
          },
        }
      )

      if (!response.ok) {
        throw new Error('Failed to fetch IGL document')
      }

      const data = await response.json()

      if (data && data.url) {
        // Open URL in new tab to trigger download
        window.open(data.url, '_blank')

        toast.success('IGL download started!', {
          position: 'top-right',
          autoClose: 3000,
        })
      } else {
        throw new Error('Invalid response format')
      }
    } catch (error) {
      console.error('Error downloading IGL:', error)
      toast.error('Failed to download IGL', {
        position: 'top-right',
        autoClose: 3000,
      })
    }
  }

  // Move fetchMyTurnTotal outside of useEffect so it can be reused
  const fetchMyTurnTotal = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_PROVIDER_URL}/api/Claims`, {
        params: {
          ProviderCode: auth?.provider?.providerCode,
          'Page.page': 1,
          'Page.pageSize': 100,
        },
        headers: {
          Authorization: `Bearer ${auth?.token} || ${user.currentUser?.token}`,
        },
      })

      if (response.data) {
        setTotalRecord(response.data)
        setIsLoading(false)
      }
    } catch (error) {
      console.error('Error fetching my turn total:', error)
      setTotalRecord({items: []}) // Set empty items on error
    }
  }

  // Update the updateClaimStatus Void
  const updateClaimStatus = async (claimNumber: string, remark: string) => {
    const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/UpdateStatus`
    const payload = {
      claimNumber: claimNumber,
      status: 'Void',
      username: user.currentUser?.email,
      source: 'Provider',
      remarkRejectVoid: remark,
    }
    const headers = {
      Authorization: `Bearer ${auth?.token} || ${user.currentUser?.token}`,
    }

    try {
      const response = await axios.post(apiUrl, payload, {headers})

      if (!response.data.isSuccess) {
        throw new Error('Failed to submit IGL Request')
      }

      toast.success('Update claim status successfully!', {
        position: 'bottom-right',
        autoClose: 3000,
      })

      // Refetch data after successful update
      setIsLoading(true)
      await fetchMyTurnTotal()
    } catch (error) {
      console.error('Error updating claim status:', error)
      toast.error('Failed to update claim status', {
        position: 'bottom-right',
        autoClose: 3000,
      })
    }
  }

  // Keep the useEffect as is, but now using the extracted function
  useEffect(() => {
    fetchMyTurnTotal()
  }, [auth?.token])

  // Update toggle sort order function to show loading state
  const toggleSortOrder = () => {
    setIsSorting(true)
    // Simulate a short delay to show the loading state
    setTimeout(() => {
      setSortOrder((prevOrder) => (prevOrder === 'oldest' ? 'newest' : 'oldest'))
      setIsSorting(false)
    }, 500) // 500ms delay for demonstration - adjust as needed
  }

  const renderBlueSection = () => {
    if (isLoading) {
      return (
        <div className='d-flex justify-content-center w-100 py-5'>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>{f({id: 'DASHBOARD.LOADING'})}</span>
          </div>
        </div>
      )
    }

    if (blueSectionItems.length > 0) {
      return blueSectionItems.map((item: any, index: number) => (
        <div key={index} className='me-4'>
          <ClaimCard
            data={item}
            variant='blue'
            status='pending'
            actions={{
              primary: {
                label: 'Request IGL',
                onClick: () => {
                  console.log('Request IGL clicked')
                },
              },
            }}
            onChatAction={handleChatAction}
            onClaimAction={handleClaimAction}
            onDownloadIGL={handleDownloadIGL}
            onUpdateClaimStatus={updateClaimStatus}
          />
        </div>
      ))
    }

    return (
      <div className='d-flex justify-content-center w-100 py-5'>
        <div className='text-muted fs-6'>{f({id: 'DASHBOARD.NO_RECENT_ITEMS'})}</div>
      </div>
    )
  }

  const renderRedSection = () => {
    if (isLoading) {
      return (
        <div className='d-flex justify-content-center w-100 py-5'>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>{f({id: 'DASHBOARD.LOADING'})}</span>
          </div>
        </div>
      )
    }

    if (isSorting) {
      return (
        <div className='d-flex justify-content-center w-100 py-5'>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>{f({id: 'DASHBOARD.SORTING'})}</span>
          </div>
          <span className='ms-3 text-muted'>{f({id: 'DASHBOARD.SORTING_ITEMS'})}</span>
        </div>
      )
    }

    if (redSectionItems.length > 0) {
      return redSectionItems.map((item: any, index: number) => (
        <div key={index} className='me-4'>
          <ClaimCard
            data={item}
            variant='red'
            status='pending'
            actions={{
              primary: {
                label: 'Request IGL',
                onClick: () => {
                  console.log('Request IGL clicked')
                },
              },
            }}
            onChatAction={handleChatAction}
            onClaimAction={handleClaimAction}
            onDownloadIGL={handleDownloadIGL}
            onUpdateClaimStatus={updateClaimStatus}
          />
        </div>
      ))
    }

    return (
      <div className='d-flex justify-content-center w-100 py-5'>
        <div className='text-muted fs-6'>{f({id: 'DASHBOARD.NO_ACTION_ITEMS'})}</div>
      </div>
    )
  }

  return (
    <>
      <div className='container-fluid px-5 py-4'>
        {/* Download Forms Section */}
        <div className='card shadow-sm mb-6'>
          <div className='card-body p-6'>
            <h3 className='fw-bolder fs-2 mb-4'>{f({id: 'DASHBOARD.DOWNLOAD_FORMS'})}</h3>
            <div className='row g-4'>
              {auth?.provider?.providerCountry.toLocaleUpperCase() === 'INDONESIA' && (
                <>
                  <div className='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-auto'>
                    <a
                      href='/documents/Guidance Provider Portal HealthMetrics v0.1.5.pdf'
                      className='btn btn-light-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2'
                      download
                    >
                      <IoDocumentTextOutline size={24} />
                      PANDUAN PENGGUNAAN PROVIDER PORTAL
                    </a>
                  </div>
                  <div className='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-auto'>
                    <a
                      href='/documents/Form KRONOLOGIS Kecelakaan dan Trauma Domestik.pdf'
                      className='btn btn-light-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2'
                      download
                    >
                      <IoDocumentTextOutline size={24} />
                      KRONOLOGI
                    </a>
                  </div>
                  <div className='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-auto'>
                    <a
                      href='/documents/FORMULIR KLAIM RAWAT JALAN.pdf'
                      className='btn btn-light-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2'
                      download
                    >
                      <IoDocumentTextOutline size={24} />
                      FORM RJ
                    </a>
                  </div>
                  <div className='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-auto'>
                    <a
                      href='/documents/Form Medis MONITORING Domestik.pdf'
                      className='btn btn-light-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2'
                      download
                    >
                      <IoDocumentTextOutline size={24} />
                      MONITORING
                    </a>
                  </div>
                  <div className='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-auto'>
                    <a
                      href='/documents/Form Lembar Medis Awal (LMA) Domestik.pdf'
                      className='btn btn-light-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2'
                      download
                    >
                      <IoDocumentTextOutline size={24} />
                      LMA
                    </a>
                  </div>
                  <div className='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-auto'>
                    <a
                      href='/documents/Form PERNYATAAN kelas Kamar Domestik.pdf'
                      className='btn btn-light-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2'
                      download
                    >
                      <IoDocumentTextOutline size={24} />
                      SP
                    </a>
                  </div>
                  <div className='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-auto'>
                    <a
                      href='/documents/Form Medis Lanjutan (Medical Enquiry) ME Domestik.pdf'
                      className='btn btn-light-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2'
                      download
                    >
                      <IoDocumentTextOutline size={24} />
                      LML
                    </a>
                  </div>
                </>
              )}

              {auth?.provider?.providerCountry.toLocaleUpperCase() !== 'INDONESIA' && (
                <>
                  <div className='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-auto'>
                    <a
                      href='/documents/Guidance Provider Portal HealthMetrics v0.1.5.pdf'
                      className='btn btn-light-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2'
                      download
                    >
                      <IoDocumentTextOutline size={24} />
                      USER GUIDE for PROVIDER PORTAL
                    </a>
                  </div>
                  <div className='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-auto'>
                    <a
                      href='/documents/en/Authorization of Benefit.pdf'
                      className='btn btn-light-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2'
                      download
                    >
                      <IoDocumentTextOutline size={24} />
                      AUTHORIZATION of BENEFITS
                    </a>
                  </div>
                  <div className='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-auto'>
                    <a
                      href='/documents/en/HAF Overseas Form.pdf'
                      className='btn btn-light-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2'
                      download
                    >
                      <IoDocumentTextOutline size={24} />
                      HAF OVERSEAS FORM
                    </a>
                  </div>
                  {/* <div className='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-auto'>
                    <a
                      href='/documents/en/IGL OVERSEAS.pdf'
                      className='btn btn-light-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2'
                      download
                    >
                      <IoDocumentTextOutline size={24} />
                      IGL OVERSEAS
                    </a>
                  </div>
                  <div className='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-auto'>
                    <a
                      href='/documents/en/FGL OVERSEAS.pdf'
                      className='btn btn-light-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2'
                      download
                    >
                      <IoDocumentTextOutline size={24} />
                      FGL OVERSEAS
                    </a>
                  </div> */}
                  {/* <div className='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-auto'>
                    <a
                      href='/documents/en/Form Medical Enquiry Surgery Report - Overseas.pdf'
                      className='btn btn-light-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2'
                      download
                    >
                      <IoDocumentTextOutline size={24} />
                      MEDICAL ENQUIRY
                    </a>
                  </div> */}
                  {/* <div className='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-auto'>
                    <a
                      href='/documents/en/REJECTION FORM Overseas.pdf'
                      className='btn btn-light-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2'
                      download
                    >
                      <IoDocumentTextOutline size={24} />
                      REJECTION FORM
                    </a>
                  </div> */}
                  <div className='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-auto'>
                    <a
                      href='/documents/en/CHRONOLOGY FORM OVERSEAS.pdf'
                      className='btn btn-light-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2'
                      download
                    >
                      <IoDocumentTextOutline size={24} />
                      CHRONOLOGY FORM (FOR ACCIDENT)
                    </a>
                  </div>
                  <div className='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-auto'>
                    <a
                    href='/documents/en/Overseas Daily Monitoring Form.pdf'
                      className='btn btn-light-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2'
                      download
                    >
                      <IoDocumentTextOutline size={24} />
                      DAILY MONITORING FORM
                    </a>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        {/* Header Section */}
        <div className='d-flex justify-content-between align-items-center mb-6'>
          <h2 className='fw-bolder fs-2 m-0'>{f({id: 'DASHBOARD.HOME'})}</h2>
          <div style={{marginTop: '0.25rem'}}>
            <ToolbarClassic claimType='INPATIENT' onModalClose={() => {}} />
          </div>
        </div>

        {/* Red Section */}
        <div className='card shadow-sm mb-6'>
          <div className='card-body p-6'>
            <div className='d-flex align-items-center mb-6'>
              <div className='d-flex align-items-center'>
                <HiOutlineLightningBolt className='text-danger me-4' size={24} />
                <h3 className='fw-bolder fs-2 m-0'>{f({id: 'DASHBOARD.NEED_ACTION'})}</h3>
              </div>
              <button
                className='btn btn-light-primary border ms-auto d-flex align-items-center gap-2 py-2 px-3'
                onClick={toggleSortOrder}
                disabled={isSorting}
              >
                {isSorting ? (
                  <>
                    <span
                      className='spinner-border spinner-border-sm'
                      role='status'
                      aria-hidden='true'
                    ></span>
                    <span style={{fontSize: '17px !important'}}>
                      {f({id: 'DASHBOARD.SORTING'})}
                    </span>
                  </>
                ) : (
                  <>
                    {sortOrder === 'oldest' ? (
                      <>
                        <GoSortDesc size={18} />{' '}
                        <span style={{fontSize: '17px !important'}}>
                          {f({id: 'DASHBOARD.NEWEST'})}
                        </span>
                      </>
                    ) : (
                      <>
                        <GoSortAsc size={18} />{' '}
                        <span style={{fontSize: '17px !important'}}>
                          {f({id: 'DASHBOARD.OLDEST'})}
                        </span>
                      </>
                    )}
                  </>
                )}
              </button>
            </div>

            <div className='d-flex overflow-auto pb-3 pt-3' style={{scrollbarWidth: 'thin'}}>
              {renderRedSection()}
            </div>
          </div>
        </div>

        {/* Blue Section */}
        <div className='card shadow-sm mb-6'>
          <div className='card-body p-6'>
            <div className='d-flex align-items-center mb-6'>
              <div className='d-flex align-items-center'>
                <IoMdTime size={24} className='me-4' color='#059BB6' />
                <h3 className='fw-bolder fs-2 m-0'>{f({id: 'DASHBOARD.RECENT'})}</h3>
              </div>
            </div>

            <div className='d-flex overflow-auto pb-3' style={{scrollbarWidth: 'thin'}}>
              {renderBlueSection()}
            </div>

            <div className='d-flex align-items-center my-6'>
              <div className='d-flex align-items-center'>
                <LuLayoutPanelLeft size={24} className='me-4' />
                <h3 className='fw-bolder fs-2 m-0'>{f({id: 'DASHBOARD.ALL_ITEMS'})}</h3>
              </div>
            </div>

            <button
              className='btn btn-light-primary py-2 px-4'
              onClick={() => {
                navigate('/claims')
              }}
            >
              {f({id: 'DASHBOARD.SEE_ALL_ITEMS'})}
            </button>
          </div>
        </div>
      </div>
      {isChatModalOpen && claimDetails && (
        <ChatModal
          isOpen={isChatModalOpen}
          onClose={() => setIsChatModalOpen(false)}
          message=''
          claimDetails={claimDetails}
        />
      )}
    </>
  )
}

const DashboardWrapper: FC = () => {
  return <DashboardPage />
}

export {DashboardWrapper}
